import { sleep } from "@/utils";

const MATERIAL_KEY_PREFIX = "material-";
const THUMBNAIL_KEY_PREFIX = "thumbnail-";
const IMAGE_WIDTH = 128;
const IMAGE_HEIGHT = 128;
const BATCH_SIZE = 25;
const BATCH_DELAY = 100;
const getMaterialKey = (organizationId, id) =>
  `${MATERIAL_KEY_PREFIX}${organizationId}-${id}`;
const getThumbnailKey = (organizationId, id) =>
  `${THUMBNAIL_KEY_PREFIX}${MATERIAL_KEY_PREFIX}${organizationId}-${id}`;

const state = {
    loadingMaterials: false,
    allMaterials: [],
    errorMaterials: "",
    fallbackListName: "MaterialPreviewerTable",
    currentlyLoadedOrganizationId: null,
};

const getters = {
    isLoadingMaterials: (state) => state.loadingMaterials,
    getAllMaterials: (state) => state.allMaterials,
    getErrorMaterials: (state) => state.errorMaterials,
    currentlyLoadedOrganizationId: (state) => state.currentlyLoadedOrganizationId,
    getFallbackListName: (state) => state.fallbackListName,
};

const mutations = {
    loadAllMaterialsFromCache(state, { selectedOrganizationId, superOrganization } = {}) {
      const materials = [];
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(MATERIAL_KEY_PREFIX)) {
          const materialDataStr = localStorage.getItem(key);
          if (!materialDataStr) return;  
          const materialDataObj = JSON.parse(materialDataStr);
          const thumbnailData = localStorage.getItem(`${THUMBNAIL_KEY_PREFIX}${key}`);
          if (thumbnailData) materialDataObj.image = thumbnailData;

          if (superOrganization !== materialDataObj.organizationId 
            && selectedOrganizationId !== materialDataObj.organizationId) {
            return;
          }

          materials.push(materialDataObj);
        }
      });
      state.allMaterials = materials;
    },
    /**
     * Removes cached materials and thumbnails that are no longer present in the new materials list.
     */
    cleanupStaleCache(_, { materials }) {
      const validMaterialKeys = new Set(materials.map(material =>
        getMaterialKey(material.organizationId, material.id)
      ));
      const validThumbnailKeys = new Set(materials.map(material =>
        getThumbnailKey(material.organizationId, material.id)
      ));
      Object.keys(localStorage).forEach((key) => {
        if (
          (key.startsWith(MATERIAL_KEY_PREFIX) && !validMaterialKeys.has(key)) ||
          (key.startsWith(`${THUMBNAIL_KEY_PREFIX}`) && !validThumbnailKeys.has(key))
        ) {
          localStorage.removeItem(key);
        }
      });
    },
    setErrorMaterials(state, errorMaterials) {
      state.errorMaterials = errorMaterials;
    },
    setLoadingMaterials(state, isLoading) {
      state.loadingMaterials = isLoading;
    },
    setCurrentlyLoadedOrganizationId(state, currentlyLoadedOrganizationId) {
      state.currentlyLoadedOrganizationId = currentlyLoadedOrganizationId;
    }
};

const actions = {
    /**
     * Initializes the component by fetching and preparing material data.
     */
    async initializeMaterials({ dispatch, commit, getters }, { selectedOrganizationId, listName } = {}) {
      commit("setLoadingMaterials", true);
      if (getters.getAllMaterials.length && selectedOrganizationId === getters.currentlyLoadedOrganizationId) {
        commit("setLoadingMaterials", false);
        return;
      }
      commit("setCurrentlyLoadedOrganizationId", selectedOrganizationId);
      await dispatch("syncMaterials", { selectedOrganizationId: selectedOrganizationId, listName });
      commit("setLoadingMaterials", false);
    },

    /**
     * Loads all of the available materials from the store.
     * Optional filtering by organization IDs.
     */
    async syncMaterials({ getters, commit, dispatch }, { selectedOrganizationId, listName } = {}) {
        const superOrganization = getters.getSuperOrg;
        try {
          commit("loadAllMaterialsFromCache", { selectedOrganizationId: selectedOrganizationId, superOrganization: superOrganization });
          const materials = await dispatch("loadAllMaterials", { selectedOrganizationId, listName, superOrganization: superOrganization });
          commit("cleanupStaleCache", { materials });
          await dispatch("cacheAllMaterials", { materials });
          await dispatch("cacheAllThumbnails", { materials });
        } catch (error) {
          console.error("Error fetching materials:", error);
          commit("setErrorMaterials", error.message || "Error fetching materials");
        } finally {
          commit("loadAllMaterialsFromCache", { selectedOrganizationId: selectedOrganizationId, superOrganization: superOrganization });
        }
    },



    /**
     * Loads all the active materials.
     */
    async loadAllMaterials({ getters, dispatch }, { selectedOrganizationId, superOrganization, listName } = {}) {
      const listNameToUse = listName || getters.getFallbackListName;
      let args = {
        listName: listNameToUse,
      };
      if (selectedOrganizationId) {
          args.add = {
            filter: {
              [listName]: `state eq a,organizationId in '${selectedOrganizationId}' '${superOrganization}'`
            },
            sort: {
              [listName]: "displayName",
            },
            limit: { 
              [listName]: 0 
            }
          };
      }
      let allMaterials = await dispatch("loadMaterials", args);
      return allMaterials;
    },
    
    /** 
     * Caches all materials using keys that include the dynamic list name.
     */
    cacheAllMaterials(_, { materials }) {
      for (const material of materials) {
        const materialObject = {
          id: material.id,
          displayName: material.displayName,
          name: material.name,
          organizationId: material.organizationId,
          type: material.type,
          state: material.state,
        };
        localStorage.setItem(
          getMaterialKey(material.organizationId, material.id),
          JSON.stringify(materialObject)
        );
      }
    },
    /** 
     * Caches all thumbnails using keys that include the dynamic list name.
     */
    async cacheAllThumbnails({ dispatch }, { materials }) {
      await dispatch("processInBatches", { 
        materials, 
        callback: async (material) => {
          if (material.previewUri) {
            await dispatch("cacheMaterialThumbnail", { material });
          }
        }
      });
    },
    /**
     * Processes the items in batches.
     */
    async processInBatches(_, { materials, callback }) {
      const results = [];
      for (let index = 0; index < materials.length; index += BATCH_SIZE) {
        const currentBatch = materials.slice(index, index + BATCH_SIZE);
        const batchResults = await Promise.all(currentBatch.map(callback));
        results.push(...batchResults);
        if (index + BATCH_SIZE < materials.length) {
          await sleep(BATCH_DELAY);
        }
      }
      return results;
    },
    /**
     * Downloads and caches a material thumbnail.
     */
    async cacheMaterialThumbnail({ dispatch, commit }, { material }) {
      try {
          const imageString = await dispatch("loadThumbnail", { material });
          if (!imageString || imageString === null) return;
          localStorage.setItem(getThumbnailKey(material.organizationId, material.id), imageString);
      } catch (error) {
          console.error("Failed to load material thumbnail", error);
          commit("setErrorMaterials", error.message || "Failed to load material thumbnail");
          return null;
      }
    },

    async loadThumbnail({ dispatch }, { material }) {
      const thumbnailURLParts = material.previewUri.split("/");
      const args = {
          id: thumbnailURLParts[0],
          key: thumbnailURLParts[1],
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
      };

      const data = await dispatch("clientDownloadAsset", args);
      
      if (!data) return null;
      const mimeType = data.headers["content-type"].toLowerCase();
      const arrayBuffer = await new Response(data.text).arrayBuffer();
      const imgBase64 = Buffer.from(arrayBuffer).toString("base64");
      return `data:${mimeType};base64,${imgBase64}`;
    }
};

export default {
    state,
    getters,
    mutations,
    actions
};
